import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

// hooks
import { usePermissions } from "hooks/usePermissions";
import { useFunds } from "queries/Funds/useFunds";
import { useProperties } from "queries/useProperties";
import useCurrentFilterset from "queries/Reports/Baseline/useCurrentFilterset";

// components
import TableLoading from "components/molecules/TableLoading";
import { ErrorPage } from "pages/ErrorState";
import { useResetRecoilState } from "recoil";
import { tableStateFamily } from "recoilStore/Table/useTableState";
import { TABLE_IDS } from "utils/constants";
import Buildings from "./Buildings";
import Funds from "./Funds";
import { useBuildingsData } from "./hooks";
import Properties from "./Properties";

import PageHeader from "./HeaderComponents/PageHeader";
import "./PortfolioPage.scss";
import TabType from "./PortfolioPage.types";
import ViewHeader from "./ViewHeader";

function PortfolioPage() {
    const { isFetching: isLoadingPermissions } = usePermissions();

    const resetBuildingsTable = useResetRecoilState(tableStateFamily(TABLE_IDS.BUILDINGS));
    const resetPropertiesTable = useResetRecoilState(tableStateFamily(TABLE_IDS.PROPERTIES));
    const resetFundsTable = useResetRecoilState(tableStateFamily(TABLE_IDS.FUNDS));

    const [searchParams, setSearchParams] = useSearchParams();
    const selectedTabFromUrl = (searchParams.get("tab") as TabType) || "buildings";

    const isValidTabType = (tab: string): tab is TabType => ["buildings", "properties", "funds"].includes(tab);

    const defaultTab: TabType = isValidTabType(selectedTabFromUrl) ? selectedTabFromUrl : "buildings";
    const [selectedTab, setSelectedTab] = useState<TabType>(defaultTab);

    useEffect(() => {
        setSearchParams({ tab: selectedTab });
    }, [selectedTab, selectedTabFromUrl]);

    const tabClickHandler = (tabName: TabType) => {
        setSearchParams({ tab: tabName });
        setSelectedTab(tabName);
        resetBuildingsTable();
        resetPropertiesTable();
        resetFundsTable();
    };
    // creating the filterset here mainly so it's preloaded and the rest of the app feels faster.
    const { isLoading: isLoadingFilterset } = useCurrentFilterset();

    const {
        isLoading: isLoadingBuildingData,
        numberOfBuildings,
        error: buildingDataError,
        ...rest
    } = useBuildingsData();

    const [selectedProperties, setSelectedProperties] = useState<Property[]>([]);

    const { isLoading: propertiesLoading, data: propertiesData, error: propertiesError } = useProperties();

    const { isLoading: fundsLoading, data: fundsData, error: fundsError } = useFunds();

    // loading the buildings here here mainly so it's preloaded and the rest of the app feels faster.
    // const { isLoading: isLoadingBuildings } = useQueryBuildings(selectedFiltersetUid, reportedVsPublishedState);

    if (isLoadingPermissions || isLoadingBuildingData || propertiesLoading || fundsLoading || isLoadingFilterset)
        return <TableLoading />;
    if (buildingDataError || propertiesError || fundsError) return <ErrorPage />;

    return (
        <div className="portfolio-page">
            <PageHeader />
            <ViewHeader
                numberOfBuildings={numberOfBuildings}
                numberOfProperties={propertiesData?.length}
                numberOfFunds={fundsData?.length}
                selectedTab={selectedTab}
                tabClickHandler={tabClickHandler}
                buildingName={rest.buildingName || undefined}
                numberOfSelectedBuildings={rest.selectedBuildingRows.length}
                selectedBuildingRows={rest.selectedBuildingRows}
                setSelectedBuildingRows={rest.setSelectedBuildingRows}
                selectedProperties={selectedProperties}
                setSelectedProperties={setSelectedProperties}
                selectedTags={rest.selectedTags}
                selectedTagsAreMixed={rest.tagsAreMixed}
                portfolioTableData={selectedTab === "buildings" ? rest.tableViewTableData : propertiesData || []}
            />
            {selectedTab === "buildings" && (
                <Buildings
                    buildingsData={{
                        isLoading: isLoadingBuildingData,
                        numberOfBuildings,
                        error: buildingDataError,
                        ...rest,
                    }}
                />
            )}
            {selectedTab === "properties" && propertiesData && (
                <Properties
                    properties={propertiesData}
                    selectedProperties={selectedProperties}
                    setSelectedProperties={setSelectedProperties}
                />
            )}
            {selectedTab === "funds" && fundsData && <Funds funds={fundsData} />}
        </div>
    );
}

export default PortfolioPage;
